import domready from "domready";
import { getJSONFromAttribute } from "../Shared/Utils";
import { createRoot } from "react-dom/client";
import React, { useEffect, useMemo, useState } from "react";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { configureStore } from "@reduxjs/toolkit";
import { Provider, useDispatch } from "react-redux";
import { apiMiddleware } from "redux-api-middleware";
import { Alert, Button, Card, Col, Row } from "react-bootstrap";
import dayjs from "dayjs";
import { loadPassengerTypes, setCancelModalVisibility, resendTickets, clearBooking, findBooking } from "./Actions";
import { getApiCallState, useStateSelector } from "./Model";
import { createReducer } from "./Reducer";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { ISettings } from "../Models";
import { getAccessToken, getSettings, updateAccessToken, updateSettings } from "../Settings";
import { createApiCallReducer } from "../ApiCalls/ApiCallReducer";
import { SetStore } from "../ApiCalls/Utils";
import { CompletePayment } from "./CompletePayment";
import { CancelBookingModal } from "./CancelBookingModal";
import { EditOptionsModal } from "./EditOptions";
import { clearApiCallState } from "../ApiCalls/Actions";
import zenscroll from "zenscroll";
import { CompensationDialog } from "./CompensationDialog";
import { TravelSummary } from "./TravelSummary";
import { getKrogenProductCodes } from "../TravelDetails/KrogenOptionsHelpers";
import { SearchForm } from "./SearchForm";

let containerElement: HTMLElement;
const strings = getStrings().component.booking;

zenscroll.setup(500, 50); //500 ms animation, 50px offset (vid intoview)

const krogenProductCodes = getKrogenProductCodes();


export const ManageBookingApp = () => {
	const dispatch = useDispatch();

	const state = useStateSelector(state => state.manageBooking);
	const apicalls = getApiCallState();
	const findBookingState = apicalls.getLoadingState("findBooking");
	const resendTicketsState = apicalls.getLoadingState("resendTickets");
	var settings = getSettings();

	const [showOptionsModal, setShowOptionsModal] = useState(false); //TODO: Ska kanske vara i state det här också.
	const [showCompensationModal, setShowCompensationModal] = useState(false); //TODO: Ska kanske vara i state det här också.

	useEffect(() => {
		if (!state.bookingNumber)
			return;

		dispatch(clearApiCallState("updateOptions"));
		dispatch(clearApiCallState("loadOptions"));
	}, [state.bookingNumber, state.email]);

	//Ladda passagerartyper så fort applikationen aktiveras
	useEffect(() => {
		if (!state.passengerTypes)
			dispatch(loadPassengerTypes());
	}, []);

	useEffect(() => {
		if (!state.bookingNumber || state.mode !== "PaymentOnly")
			return;

		if (settings.token)
			dispatch(findBooking(state.bookingNumber, null, state.mode));
		else {
			getAccessToken().then(() => {
				dispatch(findBooking(state.bookingNumber, null, state.mode));
			})
		}
	}, [state.bookingNumber, state.mode]);

	//Uppdatera token i settings om vi får en ny authtoken (som när ni laddar en bokning)
	useEffect(() => {
		if (state.authToken?.accessToken)
			updateAccessToken(state.authToken.accessToken, state.authToken.expires);
	}, [state.authToken]);

	const hasRemainingTrips = useMemo(() => {
		return (state.booking?.outbound && !state.booking.outbound.cancelled && !state.booking.outbound.hasDeparted) ||
				(state.booking?.inbound && !state.booking.inbound.cancelled && !state.booking.inbound.hasDeparted)
	}, [state.booking]);

	const paymentOnly = state.mode === "PaymentOnly";
	const allowCancel = !paymentOnly && state.booking && state.booking.canBeCancelled;
	const hasPendingOptions = [state.booking?.outbound, state.booking?.inbound].some(x => x?.segments.some(s => s.options?.some(opt => !krogenProductCodes.includes(opt.code) && opt.provisionalCount)))
	const showEditOptions = !paymentOnly && !hasPendingOptions && hasRemainingTrips;
	const showCancelButton = allowCancel && !state.booking?.hasPendingChanges;

	const showPayment = state.booking?.totalPriceToBePaid > 0;

	const getPassengerType = (code: string) => state.passengerTypes?.find(x => x.code === code)?.description || code;

	const optionsModalClosed = (optionsChanged: boolean) => {
		if (optionsChanged)
			showCompletePayment();

		setShowOptionsModal(false);
	}

	const showCompletePayment = () => {
		var completePaymentElement = document.getElementById("complete-payment");
		if (completePaymentElement)
			zenscroll.intoView(completePaymentElement);
	}


	// if (!state.appVisible)
	// 	return null;

	return <div className="reservation-dialog manage-booking">

		{ !state.hideSearchForm && state.mode !== "PaymentOnly" && <SearchForm />}

		{ findBookingState === "loading" && <Alert variant="light">{strings.loading} <SpinnerSmall /></Alert> }
		{ findBookingState === "error" && <Alert variant="warning">{strings.bookingNotFound}</Alert> }

		{state.booking && <>
			<Card>
				<Card.Header>
					{strings.bookingHeader}
					{!paymentOnly && <button className="close pull-right" type="button" aria-label="Close" onClick={() => dispatch(clearBooking())}><span aria-hidden="true">×</span></button>}
				</Card.Header>
				<Card.Body className={state.booking?.cancelled ? "text-muted" : ""}>

					<Alert variant="danger" show={state.booking?.cancelled}>{strings.cancelled}</Alert>

					<Row>
						<Col>
							<dl>
								<dt>{strings.bookingNumber}</dt>
								<dd>{state.booking.bookingNumber}</dd>
								<dt>{strings.created}</dt>
								<dd>{dayjs(state.booking.created).format("YYYY-MM-DD HH:mm")}</dd>
								{/* <dt>{strings.totalPrice}</dt>
								<dd><Price amount={state.booking.totalPrice} /></dd>
								{state.booking.totalPriceToBePaid > 0 && <>
									<dt>{strings.amountToBePaid}</dt>
									<dd><Price amount={state.booking.totalPriceToBePaid} /></dd>
								</>} */}
							</dl>
						</Col>
						<Col>
							<dl>
								<dt>{strings.passengers}</dt>
								<dd>
									<ul className="list-unstyled">
										{ state.booking.passengers.map((p, idx) => <li key={idx}>{p.firstName} {p.lastName} ({getPassengerType(p.type)})</li>)}
									</ul>

								</dd>
							</dl>
						</Col>
					</Row>
					<Row>
						{state.booking.outbound && <Col sm={6}>
							<section>
								<h2>{strings.outbound}</h2>
								<TravelSummary direction="outbound" onMealsBooked={showCompletePayment} />
							</section>
						</Col>}
						{state.booking.inbound && <Col sm={6}>
							<section>
								<h2>{strings.inbound}</h2>
								<TravelSummary direction="inbound" onMealsBooked={showCompletePayment} />
							</section>
						</Col>}
					</Row>

					{ !paymentOnly && <>
						<hr />

						<Alert variant="success" show={resendTicketsState === "loaded"} dismissible onClose={() => dispatch(clearApiCallState("resendTickets"))}>{strings.resendTicketsSuccess}</Alert>
						<Alert variant="danger" show={resendTicketsState === "error"} dismissible onClose={() => dispatch(clearApiCallState("resendTickets"))}>{strings.resendTicketsFailed}</Alert>

						{ !state.booking?.cancelled && hasRemainingTrips && <>
							{ showCancelButton && <Button variant="outline-danger" className="mr-3" onClick={() => dispatch(setCancelModalVisibility(true))}>{strings.cancelButton}</Button> }
							{ !allowCancel && <Alert variant="info" className="mb-3 mt-2"><span dangerouslySetInnerHTML={ { __html: strings.noRebookInfo }}></span></Alert> }
						</>}

						{ showEditOptions && <Button variant="outline-info" className="mr-3" onClick={() => setShowOptionsModal(true)}>{strings.functionOptions}</Button>}

						{ state.booking?.canRequestCompensation && <Button variant="outline-secondary" className="mr-3" onClick={() => setShowCompensationModal(true)}>{strings.functionCompensation}</Button> }

						{ state.booking && state.booking.totalPriceToBePaid === 0 && !state.booking.cancelled &&
							<Button variant="outline-info" disabled={resendTicketsState === "loading"} className="mr-3" onClick={() => dispatch(resendTickets(state.booking.bookingNumber))}>{strings.resendTicketsButton}{resendTicketsState === "loading" && <SpinnerSmall />}</Button>}
					</>}
				</Card.Body>
			</Card>
			{ showPayment && <CompletePayment />}
			<CancelBookingModal />
		</>}

		<EditOptionsModal show={showOptionsModal} onClosed={optionsModalClosed} />
		{ state.booking?.canRequestCompensation && <CompensationDialog show={showCompensationModal} booking={state.booking} onClosed={() => setShowCompensationModal(false)} /> }
	</div>
};

domready(() => {
	containerElement = document.getElementById("manage-booking-app");

	if (!containerElement)
		return;

	const settings = getJSONFromAttribute<ISettings>(containerElement, "data-settings", undefined);


	updateSettings(settings);

	if (settings.manageApp?.mode !== "PaymentOnly")
		setTimeout(async () => {
			await getAccessToken();
		}, 0);

	const initialState = settings.manageApp || {};

	const store = configureStore({
		reducer: {
			manageBooking: createReducer(initialState),
			apicalls: createApiCallReducer()
		},
		middleware: (getDefaultMiddleware) => [apiMiddleware, ...getDefaultMiddleware({ serializableCheck: true })],
		devTools: {name: "ManageBooking"}
	});
	SetStore(store);

	const root = createRoot(containerElement);
	root.render(
		<Provider store={store}>
			<ManageBookingApp />
		</Provider>
	);

	// subscribe("LoadBooking", event => {
	// 	const detail = (event.detail || {}) as ILoadBookingParameters;

	// 	//Om det är en sökning på en Petra-bokning -- göm dialogen
	// 	// if (detail.bookingNumber?.match(/^[A-Za-z]{3}[0-9]{4}[A-Za-z]{1}$/))
	// 	// 	store.dispatch(hideApp());
	// 	// else if (detail.bookingNumber)
	// 	store.dispatch(showApp(detail.bookingNumber, detail.email, detail.mode));
	// });
});